import React, { useState, useMemo } from 'react';
import { OV_STRINGS } from '../../constants/strings';
import { addDays, today } from '../../utils/date';
import FileUploadSection from '../FileUploadSection/FileUploadSection';
import styles from './RecipientInfoStep.module.scss';

const RECIPIENT_TYPES = [
  { value: 'self', label: OV_STRINGS.RECIPIENT_TYPE_OWNER },
  { value: 'proxy', label: OV_STRINGS.RECIPIENT_TYPE_PROXY },
  { value: 'legal_proxy', label: OV_STRINGS.RECIPIENT_TYPE_LEGAL_PROXY },
];

function formatPhone(value) {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)} - ${digits.slice(3)}`;
  return `${digits.slice(0, 3)} - ${digits.slice(3, 7)} - ${digits.slice(7)}`;
}

function RecipientInfoStep({ draftUuid, onSend, onClose, loading }) {
  const [recipientType, setRecipientType] = useState('self');
  const [ownerName, setOwnerName] = useState('');
  const [ownerRelation, setOwnerRelation] = useState('');
  const [verifierName, setVerifierName] = useState('');
  const [phone, setPhone] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [files, setFiles] = useState([]);

  const expiresAt = useMemo(() => addDays(today(), 7), []);
  const isProxy = recipientType !== 'self';

  const isValid = useMemo(() => {
    if (!confirmed) return false;
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) return false;
    if (isProxy) {
      return ownerName.trim() && ownerRelation.trim() && verifierName.trim();
    }
    return ownerName.trim() !== '';
  }, [confirmed, phone, isProxy, ownerName, ownerRelation, verifierName]);

  const handleSend = () => {
    const data = {
      verifier_relation: recipientType,
      owner_name: ownerName,
      verifier_name: isProxy ? verifierName : ownerName,
      verifier_phone: phone.replace(/\D/g, ''),
    };
    if (isProxy) data.owner_relation = ownerRelation;
    onSend?.(data, files);
  };

  return (
    <>
      <div className={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }} />
      <div className={styles.modalContent}>
        <div className={styles.modal}>

          <div className={styles.nav}>
            <div className={styles.navLeft}>
              <span className={styles.badgeBlue}>{OV_STRINGS.BADGE_LABEL_VERIFY}</span>
              <span className={styles.badgeGray}>{expiresAt} {OV_STRINGS.VALID_UNTIL}</span>
            </div>
            <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="닫기">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="#171719" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>


          <div className={styles.header}>
            <h2 className={styles.title}>{OV_STRINGS.RECIPIENT_TITLE}</h2>
            <p className={styles.desc}>{OV_STRINGS.RECIPIENT_DESC}</p>
          </div>


          <div className={styles.body}>

            <div className={styles.fieldSection}>
              <div className={styles.fieldLabel}>
                <span>{OV_STRINGS.RECIPIENT_TYPE_LABEL}</span>
                <span className={styles.required}>*</span>
              </div>
              <div className={styles.radioGroup}>
                {RECIPIENT_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    className={`${styles.radioBtn} ${recipientType === type.value ? styles.radioBtnActive : ''}`}
                    onClick={() => setRecipientType(type.value)}
                  >
                    <span className={`${styles.radioCircle} ${recipientType === type.value ? styles.radioCircleActive : ''}`} />
                    <span>{type.label}</span>
                  </button>
                ))}
              </div>
            </div>


            <div className={styles.fields}>
              {isProxy ? (
                <>
                  <div className={styles.fieldRow}>
                    <div className={styles.field}>
                      <div className={styles.fieldLabel}>
                        <span>{OV_STRINGS.RECIPIENT_OWNER_NAME}</span>
                        <span className={styles.required}>*</span>
                      </div>
                      <input
                        className={styles.input}
                        type="text"
                        placeholder={OV_STRINGS.RECIPIENT_NAME_PLACEHOLDER}
                        value={ownerName}
                        onChange={(e) => setOwnerName(e.target.value.slice(0, 30))}
                      />
                    </div>
                    <div className={styles.field}>
                      <div className={styles.fieldLabel}>
                        <span>{OV_STRINGS.RECIPIENT_OWNER_RELATION}</span>
                        <span className={styles.required}>*</span>
                      </div>
                      <input
                        className={styles.input}
                        type="text"
                        placeholder={OV_STRINGS.RECIPIENT_OWNER_RELATION_PLACEHOLDER}
                        value={ownerRelation}
                        onChange={(e) => setOwnerRelation(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={styles.field}>
                    <div className={styles.fieldLabel}>
                      <span>{OV_STRINGS.RECIPIENT_VERIFIER_NAME}</span>
                      <span className={styles.required}>*</span>
                    </div>
                    <input
                      className={styles.input}
                      type="text"
                      placeholder={OV_STRINGS.RECIPIENT_NAME_PLACEHOLDER}
                      value={verifierName}
                      onChange={(e) => setVerifierName(e.target.value.slice(0, 30))}
                    />
                  </div>
                </>
              ) : (
                <div className={styles.field}>
                  <div className={styles.fieldLabel}>
                    <span>{OV_STRINGS.RECIPIENT_NAME}</span>
                    <span className={styles.required}>*</span>
                  </div>
                  <input
                    className={styles.input}
                    type="text"
                    placeholder={OV_STRINGS.RECIPIENT_NAME_PLACEHOLDER}
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value.slice(0, 30))}
                  />
                </div>
              )}


              <div className={styles.field}>
                <div className={styles.fieldLabel}>
                  <span>{OV_STRINGS.RECIPIENT_PHONE}</span>
                  <span className={styles.required}>*</span>
                </div>
                <input
                  className={styles.input}
                  type="tel"
                  placeholder={OV_STRINGS.RECIPIENT_PHONE_PLACEHOLDER}
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                />
                <p className={styles.smsNotice}>
                  인증 받을 사람에게{' '}
                  <span className={styles.smsNoticePhone}>031-703-0229</span>
                  {' '}번호로 [DISCO] 소유자 인증 요청문자가 발송됩니다.
                </p>
              </div>
            </div>


            <div className={styles.checkboxSection}>
              <button
                type="button"
                className={styles.checkboxBtn}
                onClick={() => setConfirmed(!confirmed)}
              >
                <span className={`${styles.checkboxIcon} ${confirmed ? styles.checkboxChecked : ''}`} />
                <span className={styles.checkboxText}>{OV_STRINGS.RECIPIENT_CONFIRM_CHECKBOX}</span>
              </button>
            </div>


            <div className={styles.divider} />


            <div className={styles.attachSection}>
              <FileUploadSection
                title={OV_STRINGS.RECIPIENT_ATTACHMENT_TITLE}
                files={files}
                onFilesChange={setFiles}
              >
                {recipientType === 'legal_proxy' ? (
                  <div className={styles.attachDesc}>
                    <p>{OV_STRINGS.RECIPIENT_ATTACHMENT_DESC.legal_proxy[0]}</p>
                    <div className={styles.attachDescColumns}>
                      <div className={styles.attachDescColumn}>
                        <p className={styles.attachDescBold}>[대표]</p>
                        {OV_STRINGS.RECIPIENT_ATTACHMENT_DESC.legal_proxy.slice(3, 5).map((line, i) => (
                          <p key={i}>{line}</p>
                        ))}
                      </div>
                      <div className={styles.attachDescColumn}>
                        <p className={styles.attachDescBold}>[직원]</p>
                        {OV_STRINGS.RECIPIENT_ATTACHMENT_DESC.legal_proxy.slice(7).map((line, i) => (
                          <p key={i}>{line}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.attachDesc}>
                    {(OV_STRINGS.RECIPIENT_ATTACHMENT_DESC[recipientType] || OV_STRINGS.RECIPIENT_ATTACHMENT_DESC.self).map((line, i) => (
                      <p key={i}>{line || '\u00A0'}</p>
                    ))}
                  </div>
                )}
              </FileUploadSection>
            </div>
          </div>


          <div className={styles.actionArea}>
            <button
              type="button"
              className={`${styles.ctaBtn} ${isValid ? styles.ctaActive : ''}`}
              disabled={!isValid || loading}
              onClick={handleSend}
            >
              {OV_STRINGS.SEND_BUTTON}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default RecipientInfoStep;
